import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ArrowRight, ChevronDown, Mail, ScanLine, Rocket, Shield, Handshake } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { faqData, howItWorksData, companyInfo } from '../data/mock';
import '../styles/landing.css';


const API = "/api";

const iconMap = {
  scan: ScanLine,
  rocket: Rocket,
  shield: Shield,
  handshake: Handshake
};

const LandingPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);
  const faqRef = useRef(null);

  // Glowing cursor trail effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Scroll animation observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || isLoading) return;

    setIsLoading(true);
    setErrorMessage('');

    try {
      const response = await axios.post(`${API}/subscribe`, { email });
      setIsSubmitted(true);
      setSuccessMessage(response.data.message);
      setTimeout(() => {
        setIsSubmitted(false);
        setEmail('');
        setSuccessMessage('');
      }, 4000);
    } catch (error) {
      console.error('Subscription error:', error);
      if (error.response?.status === 422) {
        setErrorMessage('Please enter a valid email address');
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="landing-container">
      {/* Cursor Trail */}
      <div
        className="cursor-glow"
        style={{
          left: mousePosition.x - 150,
          top: mousePosition.y - 150,
        }}
      />

      {/* Radial Gradient Overlay */}
      <div className="radial-gradient-overlay" />

      {/* Header */}
      <header className="header">
        <div className="header-content">
          <img
            src={companyInfo.logoUrl}
            alt="humman logo"
            className="logo"
          />
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section" ref={heroRef}>
        <div className="hero-content animate-on-scroll">
          <h1 className="hero-headline">
            Invest in the engine,<br />
            <span className="gradient-text">not the machine</span>
          </h1>
          <p className="hero-subtitle">
            {companyInfo.subtitle}
          </p>

          {/* Lead Capture Form */}
          <form className="lead-form" onSubmit={handleSubmit}>
            <div className="input-wrapper">
              <Mail className="input-icon" size={20} />
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="email-input"
                required
                disabled={isLoading}
              />
            </div>
            <button
              type="submit"
              className={`notify-btn ${isSubmitted ? 'submitted' : ''} ${isLoading ? 'loading' : ''}`}
              disabled={isSubmitted || isLoading}
            >
              {isLoading ? 'Sending...' : isSubmitted ? 'Joined!' : 'Join the Waitlist'}
              {!isSubmitted && !isLoading && <ArrowRight size={18} />}
            </button>
          </form>

          {successMessage && (
            <p className="success-message animate-on-scroll">
              {successMessage}
            </p>
          )}

          {errorMessage && (
            <p className="error-message animate-on-scroll">
              {errorMessage}
            </p>
          )}
        </div>

        {/* Scroll indicator */}
        <div className="scroll-indicator animate-on-scroll">
          <span>Learn more</span>
          <ChevronDown className="bounce" size={24} />
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="section-container animate-on-scroll">
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">From application to partnership in four steps</p>
          
          <div className="steps-grid">
            {howItWorksData.map((step, index) => {
              const IconComponent = iconMap[step.icon];
              return (
                <div key={step.id} className="step-card animate-on-scroll" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="step-number">{String(index + 1).padStart(2, '0')}</div>
                  <div className="step-icon">
                    <IconComponent size={28} />
                  </div>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section" ref={faqRef}>
        <div className="faq-container">
          {/* How It Works FAQs */}
          <div className="faq-group animate-on-scroll">
            <h2 className="faq-title">How It Works</h2>
            <p className="faq-subtitle">Understanding the Human Capital Protocol</p>
            
            <Accordion type="single" collapsible className="faq-accordion">
              {faqData.how_it_works.map((item, index) => (
                <AccordionItem
                  key={item.id}
                  value={`works-${item.id}`}
                  className="faq-item animate-on-scroll"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <AccordionTrigger className="faq-question">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="faq-answer">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* How It Doesn't Work FAQs */}
          <div className="faq-group animate-on-scroll">
            <h2 className="faq-title">What This Is Not</h2>
            <p className="faq-subtitle">Common misconceptions clarified</p>
            
            <Accordion type="single" collapsible className="faq-accordion">
              {faqData.how_it_doesnt.map((item, index) => (
                <AccordionItem
                  key={item.id}
                  value={`doesnt-${item.id}`}
                  className="faq-item animate-on-scroll"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <AccordionTrigger className="faq-question">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="faq-answer">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <img
            src={companyInfo.logoUrl}
            alt="humman logo"
            className="footer-logo"
          />
          <p className="footer-text">
            © {new Date().getFullYear()} humman. The Human Capital Protocol.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
